/**
 * Marketing attribution vocabulary, shared by the Convex backend and the browser.
 *
 * @overview
 * One definition of "who sent this customer", imported by both sides so the
 * frontend cannot tag an order with a source the margin queries do not know
 * about. A silent divergence here would mis-bucket revenue between D2C and
 * Amazon, which is precisely the number the paid-social channel is judged on.
 *
 * Kept free of any `convex/` server import so it is safe to pull into the
 * browser bundle. Import it with a RELATIVE path from src/ (e.g.
 * `../../convex/lib/attribution`), matching how the rest of the frontend imports
 * convex/_generated. The `@convex/*` alias in tsconfig.json looks like the tidier
 * option and does NOT work — see frontend/AGENTS.md.
 *
 * @usage
 * ```typescript
 * import { classifyAttribution, ORDER_ATTRIBUTION_SOURCES } from '../../convex/lib/attribution';
 *
 * const source = classifyAttribution({ fbclid: params.get('fbclid') });
 * ```
 */

/**
 * Who gets credit for an order.
 *
 * Deliberately distinct from `orders.fulfillmentMethod` ("amazon-mcf" | "self"),
 * which says who SHIPS the order. The two are independent: a self-fulfilled
 * order can be Amazon-originated, and only a non-Amazon-originated order avoids
 * Amazon's referral fee.
 */
export const ORDER_ATTRIBUTION_SOURCES = {
  /** Arrived from a Meta ad — carries an fbclid or Meta utm_source. */
  META_PAID: "meta_paid",
  /** Arrived through the collector quiz recommendation, unpaid. */
  QUIZ: "quiz",
  /** Direct, search, or any other unpaid non-Amazon entry. */
  ORGANIC: "organic",
  /**
   * Scanned a printed insert-card QR code.
   *
   * Distinct from AMAZON even though the card ships inside an Amazon parcel.
   * AMAZON means the ORDER happened on Amazon and paid a referral fee; an
   * insert scan sends the customer to our own storefront, where it does not.
   * Folding the two together would charge insert-driven revenue a fee it never
   * paid and understate the D2C margin premium — the exact number this channel
   * is judged on.
   */
  INSERT: "insert",
  /** Originated on Amazon (e.g. an order placed in the marketplace itself). */
  AMAZON: "amazon",
  /** Attribution could not be determined. Never silently folded into organic. */
  UNKNOWN: "unknown",
} as const;

export type OrderAttributionSource =
  (typeof ORDER_ATTRIBUTION_SOURCES)[keyof typeof ORDER_ATTRIBUTION_SOURCES];

const VALID_SOURCES: ReadonlySet<string> = new Set(
  Object.values(ORDER_ATTRIBUTION_SOURCES)
);

/** True when `value` is a source the margin queries understand. */
export function isOrderAttributionSource(
  value: string | undefined | null
): value is OrderAttributionSource {
  return typeof value === "string" && VALID_SOURCES.has(value);
}

/**
 * Narrow an untrusted string to a known source.
 *
 * Attribution arrives from the browser (query params a customer can edit), so
 * an unrecognised value is coerced to UNKNOWN rather than trusted into the
 * revenue split.
 */
export function coerceAttributionSource(
  value: string | undefined | null
): OrderAttributionSource {
  return isOrderAttributionSource(value)
    ? value
    : ORDER_ATTRIBUTION_SOURCES.UNKNOWN;
}

/** Sources that cost money to acquire, and therefore belong in a CAC figure. */
export function isPaidAcquisition(source: string): boolean {
  return source === ORDER_ATTRIBUTION_SOURCES.META_PAID;
}

/**
 * Sources that avoid Amazon's referral fee, i.e. earn the D2C margin premium.
 *
 * AMAZON is excluded for the obvious reason; UNKNOWN is excluded because
 * crediting unclassified revenue with the premium would overstate the exact
 * number this channel is being judged on.
 */
export function earnsD2cMarginPremium(source: string): boolean {
  return (
    source === ORDER_ATTRIBUTION_SOURCES.META_PAID ||
    source === ORDER_ATTRIBUTION_SOURCES.QUIZ ||
    // The insert is printed and shipped by us; the ORDER it produces lands on
    // our storefront and pays Amazon nothing. Note this is deliberately NOT in
    // `isPaidAcquisition`: inserts do cost money, but it is a fixed print run,
    // not per-acquisition spend, and there is no insert-spend input for a CAC
    // denominator to divide by. Adding it there would produce a CAC computed
    // against zero spend.
    source === ORDER_ATTRIBUTION_SOURCES.INSERT ||
    source === ORDER_ATTRIBUTION_SOURCES.ORGANIC
  );
}

/** Signals available at the moment a visitor lands, used to classify them. */
export interface AttributionSignals {
  /** Meta's click identifier, present on any click-through from a Meta ad. */
  fbclid?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  /** `document.referrer`, used only as a weak fallback. */
  referrer?: string | null;
}

const META_UTM_SOURCES = new Set(["facebook", "instagram", "meta", "fb", "ig"]);
/** Stamped by the insert-card redirect in `http.ts`. Kept in one place so the
 *  minting side and the reading side cannot drift apart. */
const INSERT_UTM_SOURCE = "insert";
const QR_UTM_MEDIUM = "qr";
const PAID_UTM_MEDIUMS = new Set(["cpc", "paid", "paid_social", "ppc"]);

/**
 * Decide which channel a landing belongs to.
 *
 * Order matters and encodes precedence, strongest signal first:
 *   1. `fbclid` — Meta stamps it on ad click-throughs; it is the only signal
 *      that is hard for an unrelated visit to produce by accident.
 *   2. Our own insert-card utm tags — a printed QR scan, which carries no
 *      referrer at all and would otherwise be indistinguishable from direct.
 *   3. Meta utm_source paired with a paid medium — tagged links we control.
 *   4. An Amazon referrer — the marketplace path.
 *   5. Any other referrer or none — organic.
 *
 * A Meta utm_source WITHOUT a paid medium is treated as organic social, not
 * paid: crediting an organic Instagram post to ad spend would flatter ROAS.
 */
export function classifyAttribution(
  signals: AttributionSignals
): OrderAttributionSource {
  const { fbclid, utmSource, utmMedium, referrer } = signals;

  if (fbclid && fbclid.trim().length > 0) {
    return ORDER_ATTRIBUTION_SOURCES.META_PAID;
  }

  const source = utmSource?.trim().toLowerCase();
  const medium = utmMedium?.trim().toLowerCase();

  // A printed QR scan arrives with NO referrer and no click id — a phone's
  // camera opens the URL directly — so these utm values are the only signal
  // there is. Without this branch every insert scan classified as `organic`,
  // which is why insert-driven revenue was previously indistinguishable from
  // someone typing the domain in. We mint these URLs ourselves (see
  // scripts/generate-qr-codes.mjs), so the values are ours, not a guess.
  if (source === INSERT_UTM_SOURCE || medium === QR_UTM_MEDIUM) {
    return ORDER_ATTRIBUTION_SOURCES.INSERT;
  }

  if (source && META_UTM_SOURCES.has(source)) {
    return medium && PAID_UTM_MEDIUMS.has(medium)
      ? ORDER_ATTRIBUTION_SOURCES.META_PAID
      : ORDER_ATTRIBUTION_SOURCES.ORGANIC;
  }

  if (referrer && isAmazonHostname(safeHostname(referrer))) {
    return ORDER_ATTRIBUTION_SOURCES.AMAZON;
  }

  return ORDER_ATTRIBUTION_SOURCES.ORGANIC;
}

/** Hostname of `url`, or "" when it is not parseable. Never throws. */
function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

/** Second-level labels Amazon actually uses under a country code. */
const AMAZON_SECOND_LEVEL = new Set(["co", "com"]);

/**
 * True for a genuine Amazon storefront host.
 *
 * Matches on domain LABELS rather than a substring or a trailing-wildcard
 * regex. The naive `/amazon\.[a-z.]+$/` form accepts `amazon.com.evil.test`,
 * which lets anyone who controls a referring page book their traffic as
 * Amazon-originated — and since Amazon-originated revenue is exactly the
 * revenue excluded from the D2C margin premium, that is a corruptible number,
 * not a cosmetic bug.
 */
function isAmazonHostname(hostname: string): boolean {
  const labels = hostname.toLowerCase().split(".");
  const amazonIndex = labels.lastIndexOf("amazon");
  if (amazonIndex === -1) return false;

  // Everything after the "amazon" label must be a plausible public suffix:
  // amazon.com, amazon.co.uk, amazon.com.au — but never amazon.com.evil.test.
  const suffix = labels.slice(amazonIndex + 1);
  if (suffix.length === 1) {
    return /^[a-z]{2,3}$/.test(suffix[0]);
  }
  if (suffix.length === 2) {
    return AMAZON_SECOND_LEVEL.has(suffix[0]) && /^[a-z]{2}$/.test(suffix[1]);
  }
  return false;
}

/**
 * Build Meta's `fbc` click-identifier parameter.
 *
 * `fbc` is the strongest signal Meta has for attributing a conversion back to
 * the ad that caused it — and it is NOT the raw `fbclid`. Meta's format is
 * `fb.<subdomainIndex>.<creationTime>.<fbclid>`, e.g.
 * `fb.1.1700000000000.IwAR0abc`. Sending a bare `fbclid` is accepted by the API
 * and then matches nothing, which fails silently: events arrive, match quality
 * is quietly poor, and reported ROAS understates reality with nothing in the
 * logs to explain it.
 *
 * `subdomainIndex` is 1 for a cookie set on `example.com` (counting from the
 * TLD: com=0, example=1). Our pixel runs on `www.infinityvaultcards.com` with
 * the cookie scoped to the registrable domain, so 1 is correct.
 *
 * Prefer the real `_fbc` cookie when the browser has one — the pixel writes it
 * with Meta's own timestamp, which matches better than a value we reconstruct.
 * `cookieValue` is passed through untouched when it already looks well-formed.
 */
export function buildFbc(
  fbclid: string | undefined | null,
  clickTimeMs: number,
  cookieValue?: string | null
): string | undefined {
  // Trust a well-formed cookie over anything we can rebuild.
  if (cookieValue && /^fb\.\d+\.\d+\..+/.test(cookieValue)) {
    return cookieValue;
  }
  const id = fbclid?.trim();
  if (!id) return undefined;
  return `fb.1.${Math.floor(clickTimeMs)}.${id}`;
}

/**
 * Meta standard event names.
 *
 * Standard names (not custom events) so Meta's optimisation and reporting can
 * use them without extra configuration in Events Manager.
 */
export const META_EVENTS = {
  VIEW_CONTENT: "ViewContent",
  LEAD: "Lead",
  ADD_TO_CART: "AddToCart",
  INITIATE_CHECKOUT: "InitiateCheckout",
  PURCHASE: "Purchase",
} as const;

export type MetaEventName = (typeof META_EVENTS)[keyof typeof META_EVENTS];

/**
 * Dedup key shared between the browser pixel and the server relay.
 *
 * Meta discards the server copy of an event when a pixel event with the same
 * `event_id` already arrived, so this MUST be deterministic for a given
 * conversion — deriving it from a stable business key (the order number)
 * rather than a timestamp or random id is what makes the dedup work.
 */
export function buildEventId(eventName: MetaEventName, key: string): string {
  return `${eventName.toLowerCase()}:${key}`;
}
